import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, TrendingUp, Mail, MessageSquare, BookOpen, Download } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { exportToCSV, formatReportData } from "@/lib/export-utils";

export const Analytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalDiscussions: 0,
    totalResources: 0,
    newsletterSubscribers: 0,
    recentSignups: [] as any[],
    recentEvents: [] as any[],
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }
    
    // Check if user has admin role
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .single();
    
    if (!roleData) {
      toast.error("Access denied. Admin privileges required.");
      navigate("/dashboard");
      return;
    }
    
    loadAnalytics();
  };

  const loadAnalytics = async () => {
    // Load all analytics data
    const [
      { count: userCount },
      { count: discussionCount },
      { count: resourceCount },
      { count: newsletterCount },
      { data: recentProfiles },
      { data: recentEvents },
    ] = await Promise.all([
      supabase.from("profiles").select("*", { count: "exact", head: true }),
      supabase.from("discussions").select("*", { count: "exact", head: true }),
      supabase.from("resources").select("*", { count: "exact", head: true }),
      supabase.from("newsletter_subscribers").select("*", { count: "exact", head: true }),
      supabase.from("profiles").select("*").order("created_at", { ascending: false }).limit(5),
      supabase.from("analytics_events").select("*").order("created_at", { ascending: false }).limit(10),
    ]);

    setStats({
      totalUsers: userCount || 0,
      totalDiscussions: discussionCount || 0,
      totalResources: resourceCount || 0,
      newsletterSubscribers: newsletterCount || 0,
      recentSignups: recentProfiles || [],
      recentEvents: recentEvents || [],
    });

    setLoading(false);
  };

  const statCards = [
    { icon: Users, label: "Total Members", value: stats.totalUsers, color: "text-blue-500" },
    { icon: MessageSquare, label: "Discussions", value: stats.totalDiscussions, color: "text-green-500" },
    { icon: BookOpen, label: "Resources", value: stats.totalResources, color: "text-purple-500" },
    { icon: Mail, label: "Newsletter Subscribers", value: stats.newsletterSubscribers, color: "text-orange-500" },
  ];

  const handleDownloadAnalytics = () => {
    const formattedData = formatReportData(stats);
    const filename = `analytics_${new Date().toISOString().split('T')[0]}`;
    exportToCSV(formattedData, filename);
    
    toast.success("Analytics report downloaded successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Analytics Dashboard</h1>
              <p className="text-muted-foreground">Track your community growth and engagement</p>
            </div>
            <Button onClick={handleDownloadAnalytics} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Download Analytics
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat) => (
              <Card key={stat.label}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.label}
                  </CardTitle>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recent Activity */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Signups */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Recent Signups
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.recentSignups.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No recent signups</p>
                ) : (
                  <div className="space-y-4">
                    {stats.recentSignups.map((signup) => (
                      <div key={signup.id} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{signup.username || "Anonymous"}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(signup.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Events */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.recentEvents.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No recent activity</p>
                ) : (
                  <div className="space-y-4">
                    {stats.recentEvents.map((event) => (
                      <div key={event.id} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium capitalize">{event.event_type.replace("_", " ")}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
